import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../i18n/localeUtils'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import { LoaderCircle, X } from 'lucide-react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { WatchListItem, WatchListItemType } from '@finance-lab/utils/types'
import { WatchListsApi } from '../../api/WatchListApi'
import { AddWatchItem } from './AddWatchItem'
import { userMessageAtom, userMessageStore } from '../../atoms/userMessageAtom'
import { formatDateYYYYMMDD } from '../../packages/utils/numberFormatters'
import IssueTypeLabels, { HasWarrantLabel, KVLabel } from '../../components/entities/IssueTypeLabels'
import Stack from '@mui/material/Stack'
import { Link } from 'react-router'
import { getUrlForType } from '../../utils/getUrlForEntity'
import Skeleton from '@mui/material/Skeleton'
import UserEventApi from '../../api/UserEventApi'
import { TFunction } from 'i18next'

export const getGroupLabel = (type: WatchListItemType, t: TFunction) =>
    t(`pages.watchLists.groupLabels.${type}`)

const watchListApi = new WatchListsApi()
const userEventApi = new UserEventApi()

export const WatchListGroup = ({
    items,
    isLoading,
    onChange,
    type,
}: {
    items: WatchListItem[]
    isLoading: boolean
    onChange: () => Promise<void>
    type: WatchListItemType
}) => {
    const { t } = useTranslation()
    const { language } = useAppLocale()
    const [pendingDeleteItemId, setPendingDeleteItemId] = useState<string | null>(null)
    const groupLabel = getGroupLabel(type, t)
    const typeLowercase = groupLabel.toLowerCase()

    const handleDelete = (item: WatchListItem) => {
        void (async () => {
            setPendingDeleteItemId(item.id)

            try {
                await watchListApi.deleteItem(type, item.entityId)
                await onChange()
                userMessageStore.set(userMessageAtom, {
                    type: 'success',
                    message: t('pages.watchLists.removedSuccess', { name: item.name }),
                })
            } catch (err) {
                console.error(err)
            } finally {
                setPendingDeleteItemId(null)
            }

            await userEventApi.logEvent(UserEventApi.EVENT_TYPES.DELETE_WATCH_LIST_ITEM)
        })()
    }

    return (
        <Accordion sx={{ paddingX: 4 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} disabled={isLoading}>
                <Typography variant='h6'>
                    {isLoading ? <Skeleton height={24} width={200} /> : `${groupLabel} (${items.length})`}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <AddWatchItem onChange={onChange} type={type} />
                {items.length === 0 && (
                    <p>{t('pages.watchLists.emptyMessage', { type: typeLowercase })}</p>
                )}

                {items.length > 0 && (
                    <>
                        <p style={{ fontWeight: 'bold' }}>
                            {t('pages.watchLists.watchedHeading', { type: typeLowercase })}
                        </p>
                        {items.map((item) => (
                            <Stack
                                key={item.id}
                                direction='row'
                                sx={{
                                    alignItems: 'center',
                                }}
                            >
                                <>
                                    <Tooltip title={t('pages.watchLists.removeTooltip', { name: item.name })}>
                                        <IconButton onClick={() => handleDelete(item)} sx={{ marginRight: 4 }}>
                                            {pendingDeleteItemId === item.id ? (
                                                <LoaderCircle size={15} />
                                            ) : (
                                                <X color='red' size={15} />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                    {['Issue', 'Warrant', 'Convertible'].includes(type) ? null : (
                                        <Link to={getUrlForType({ entityType: type, id: item.entityId })}>
                                            {item.name}
                                        </Link>
                                    )}

                                    {type !== 'Warrant' ? null : (
                                        <>
                                            <div style={{ minWidth: 100 }}>
                                                <HasWarrantLabel />
                                            </div>
                                            <Link to={getUrlForType({ entityType: type, id: item.entityId })}>
                                                {item.name}
                                            </Link>
                                        </>
                                    )}

                                    {type !== 'Issue' ? null : (
                                        <>
                                            <div style={{ minWidth: 100 }}>
                                                {formatDateYYYYMMDD(
                                                    item.issueData.ipoTradingDate ||
                                                        item.issueData.subEndDate ||
                                                        item.issueData.publicationDate,
                                                    { locale: language },
                                                )}
                                            </div>
                                            <div style={{ minWidth: 100 }}>
                                                <IssueTypeLabels issue={item.issueData} />
                                            </div>
                                            <Link to={`/issues/${item.issueId}`} style={{ flexGrow: 1 }}>
                                                {item.name}
                                            </Link>
                                        </>
                                    )}
                                    {type !== 'Convertible' ? null : (
                                        <>
                                            <div style={{ minWidth: 100 }}>
                                                <KVLabel />
                                            </div>
                                            <Link to={`/convertibles/${item.convertibleId}`} style={{ flexGrow: 1 }}>
                                                {item.convertibleName}
                                            </Link>
                                        </>
                                    )}
                                </>
                            </Stack>
                        ))}
                    </>
                )}
            </AccordionDetails>
        </Accordion>
    )
}
